import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, Pencil, Ban, Trash2, BadgeCheck, AlertTriangle, KeyRound, Save, Flag, PlusCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { COUNTRIES, CITIES_BY_COUNTRY, countryFlag } from "@/lib/places";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { can, STAFF_PERMS } from "@/lib/perms";

function StaffPanel({ u, id, onDone }) {
  const [perms, setPerms] = useState(u.perms || []);
  const [saving, setSaving] = useState(false);
  const toggle = (k) => setPerms((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const save = async (list) => {
    setSaving(true);
    try { await api.put(`/admin/users/${id}/staff`, { perms: list }); toast.success(list.length ? "Admini u ruajt" : "Qasja u hoq"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gabim"); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-amber-200 p-4 mt-3" data-testid="aud-staff-panel">
      <div className="text-sm font-bold text-slate-900">Bëje admin</div>
      <p className="text-[11px] text-slate-500 mb-2">Zgjidh çfarë mund të hapë dhe ndryshojë. Çdo ndryshim i tij shihet te Log.</p>
      <div className="grid grid-cols-2 gap-1.5">
        {STAFF_PERMS.map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-xs text-slate-700 bg-slate-50 rounded-lg px-2 py-2">
            <input data-testid={`staff-perm-${k}`} type="checkbox" checked={perms.includes(k)} onChange={() => toggle(k)} className="accent-emerald-600" />{l}
          </label>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <button data-testid="staff-all" onClick={() => setPerms(STAFF_PERMS.map(([k]) => k))} className="h-10 px-3 rounded-xl bg-slate-100 text-slate-700 text-xs font-semibold">Të gjitha</button>
        <button data-testid="staff-save" disabled={saving || !perms.length} onClick={() => save(perms)} className="flex-1 h-10 rounded-xl bg-emerald-500 text-white text-xs font-bold disabled:opacity-50">Ruaj qasjen</button>
        {u.role === "staff" && <button data-testid="staff-remove" disabled={saving} onClick={() => save([])} className="h-10 px-3 rounded-xl bg-rose-50 text-rose-600 text-xs font-bold">Hiq</button>}
      </div>
    </div>
  );
}

export default function AdminUserDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { confirm, prompt } = useConfirm();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);

  const load = () => api.get(`/admin/users/${id}`).then((r) => { setData(r.data); setForm(r.data.user); }).catch(() => setData(false));
  useEffect(() => { if (can(user, "users")) load(); }, [id, user]); // eslint-disable-line

  if (user && user !== false && !can(user, "users")) return <div className="p-8 text-center text-slate-500">Vetëm admini ka qasje.</div>;
  if (data === false) return <div className="p-8 text-center text-slate-500">Përdoruesi nuk u gjet.</div>;
  if (!data) return <div className="p-8 text-center text-slate-400">Duke ngarkuar...</div>;

  const u = data.user;
  const locked = (u.role === "admin" || u.role === "staff") && user?.role !== "admin";
  const safe = (fn) => async () => { try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || "Gabim"); } };
  const block = safe(async () => {
    if (u.blocked) { await api.post(`/admin/users/${id}/block`); toast.success("U zhbllokua"); return load(); }
    const reason = await prompt({ title: "Blloko përdoruesin", placeholder: "Arsyeja e bllokimit", confirmText: "Blloko", danger: true });
    if (reason === null) return;
    await api.post(`/admin/users/${id}/block`, { reason });
    toast.success("U bllokua"); load();
  });
  const warn = safe(async () => { const text = await prompt({ title: "Paralajmëro përdoruesin", placeholder: "Teksti i paralajmërimit", required: true, confirmText: "Dërgo" }); if (!text) return; await api.post(`/admin/users/${id}/warn`, { text }); toast.success("Paralajmërimi u dërgua"); load(); });
  const verifyAcc = safe(async () => { await api.post(`/admin/users/${id}/verify-account`); toast.success("Llogaria u verifikua"); load(); });
  const recovery = safe(async () => { const r = await api.post(`/admin/users/${id}/recovery`); toast.success(r.data.sent ? "Linku i rikuperimit u dërgua në email" : "Linku u krijua (email s'u dërgua)"); });
  const del = safe(async () => { if (!(await confirm({ title: "Fshi llogarinë", message: "Llogaria dhe shpalljet e saj kalojnë te Koshi dhe mund të kthehen brenda 60 ditëve." }))) return; await api.delete(`/admin/users/${id}`); toast.success("Llogaria u fshi"); nav("/admin"); });
  const save = async () => {
    try { const r = await api.put(`/admin/users/${id}`, form); setData((d) => ({ ...d, user: r.data })); setEditing(false); toast.success("U ruajt"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gabim"); }
  };

  const cities = form?.country ? Object.keys(CITIES_BY_COUNTRY[form.country] || {}) : [];
  const Btn = ({ onClick, icon: Icon, label, color, tid }) => (
    <button data-testid={tid} onClick={onClick} className={`flex flex-col items-center gap-1 rounded-xl border border-slate-100 bg-white p-3 ${color}`}><Icon className="w-5 h-5" /><span className="text-[11px] font-semibold">{label}</span></button>
  );

  return (
    <div className="gs-fade px-4 pt-4 pb-6" data-testid="admin-user-detail">
      <button onClick={() => nav("/admin")} className="flex items-center gap-1 text-sm text-slate-500 mb-2"><ArrowLeft className="w-4 h-4" />Admin</button>

      <div className="bg-white rounded-3xl p-5 border border-slate-100">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-2xl font-bold overflow-hidden shrink-0">
            {u.avatar ? <img src={u.avatar} alt="" className="w-full h-full object-cover" /> : (u.name?.[0] || "?")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1"><h1 className="text-lg font-extrabold text-slate-900 truncate" data-testid="aud-name">{u.name} {u.surname}</h1>{u.verified && <BadgeCheck className="w-4 h-4 text-sky-500" />}</div>
            <div className="text-xs text-slate-400 truncate">{u.email}</div>
            <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${u.blocked ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-700"}`}>{u.blocked ? "Bllokuar" : "Aktiv"}</span>
          </div>
        </div>
        {u.block_reason && <p className="text-xs text-red-500 mt-2">Arsyeja e bllokimit: {u.block_reason}</p>}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-sm">
          <div className="text-slate-400">Shteti</div><div className="text-slate-700 text-right">{countryFlag(u.country)} {u.country || "—"}</div>
          <div className="text-slate-400">Qyteti</div><div className="text-slate-700 text-right">{u.city || "—"}</div>
          <div className="text-slate-400">Adresa</div><div className="text-slate-700 text-right truncate">{u.address || "—"}</div>
          <div className="text-slate-400">Lloji</div><div className="text-slate-700 text-right">{u.account_type === "private" ? "Privat" : "Biznis"}</div>
          <div className="text-slate-400">Regjistruar</div><div className="text-slate-700 text-right">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3">
        <Btn tid="aud-view" onClick={() => nav(`/perdorues/${id}`)} icon={Eye} label="Shiko profilin" color="text-slate-700" />
        {!locked && <>
        <Btn tid="aud-edit" onClick={() => setEditing((e) => !e)} icon={Pencil} label="Ndrysho" color="text-blue-600" />
        <Btn tid="aud-publish-for" onClick={() => nav(`/publiko?per=${id}`)} icon={PlusCircle} label="Publiko për të" color="text-emerald-600" />
        <Btn tid="aud-recovery" onClick={recovery} icon={KeyRound} label="Rikuperim" color="text-amber-600" />
        {!u.verified && <Btn tid="aud-verify" onClick={verifyAcc} icon={BadgeCheck} label="Verifiko" color="text-sky-600" />}
        <Btn tid="aud-warn" onClick={warn} icon={AlertTriangle} label="Paralajmëro" color="text-orange-600" />
        <Btn tid="aud-block" onClick={block} icon={Ban} label={u.blocked ? "Zhblloko" : "Blloko"} color={u.blocked ? "text-emerald-600" : "text-red-500"} />
        <Btn tid="aud-delete" onClick={del} icon={Trash2} label="Fshi" color="text-red-500" />
        </>}
      </div>

      {user?.role === "admin" && u.role !== "admin" && <StaffPanel key={u.role + (u.perms || []).join()} u={u} id={id} onDone={load} />}

      {editing && form && (
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mt-3 space-y-2" data-testid="aud-edit-form">
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="aud-f-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Emri" className="rounded-xl h-10" />
            <Input data-testid="aud-f-surname" value={form.surname || ""} onChange={(e) => setForm({ ...form, surname: e.target.value })} placeholder="Mbiemri" className="rounded-xl h-10" />
          </div>
          <Input data-testid="aud-f-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="rounded-xl h-10" />
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="aud-f-country" value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value, city: "" })} className="h-10 w-full border border-slate-200 rounded-xl px-2 bg-white text-sm">
              <option value="">Shteti</option>{COUNTRIES.map((c) => <option key={c.name} value={c.name}>{c.flag} {c.name}</option>)}
            </select>
            <select data-testid="aud-f-city" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-10 w-full border border-slate-200 rounded-xl px-2 bg-white text-sm">
              <option value="">Qyteti</option>{cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Input data-testid="aud-f-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Adresa" className="rounded-xl h-10" />
          <select data-testid="aud-f-type" value={form.account_type || "private"} onChange={(e) => setForm({ ...form, account_type: e.target.value })} className="h-10 w-full border border-slate-200 rounded-xl px-2 bg-white text-sm">
            <option value="private">Privat</option><option value="business">Biznis</option>
          </select>
          <Button data-testid="aud-save" onClick={save} className="w-full h-10 rounded-xl bg-slate-900 hover:bg-slate-800"><Save className="w-4 h-4 mr-1" />Ruaj</Button>
        </div>
      )}

      <h2 className="font-bold text-slate-800 mt-6 mb-2">Shpalljet ({data.listings.length})</h2>
      <div className="space-y-2">
        {data.listings.map((l) => (
          <div key={l.id} onClick={() => nav(l.category === "punekerkues" ? `/punekerkues/${l.id}` : `/shpallje/${l.id}`)} className="flex items-center gap-2 bg-white rounded-xl p-2.5 border border-slate-100">
            <div className="flex-1 min-w-0"><div className="font-semibold text-sm text-slate-800 truncate">{l.title}</div><div className="text-xs text-slate-400">{l.category} · {l.city} · {l.status}</div></div>
          </div>
        ))}
        {!data.listings.length && <p className="text-slate-400 text-sm">Pa shpallje.</p>}
      </div>

      <h2 className="flex items-center gap-1 font-bold text-slate-800 mt-6 mb-2"><Flag className="w-4 h-4 text-red-400" />Raportimet ndaj tij ({data.reports.length})</h2>
      <div className="space-y-2">
        {data.reports.map((r) => (
          <div key={r.id} className="bg-white rounded-xl p-2.5 border border-slate-100">
            <div className="text-sm text-slate-800">{r.reason}</div>
            <div className="text-[11px] text-slate-400">nga {r.reporter_name} · {r.status}</div>
          </div>
        ))}
        {!data.reports.length && <p className="text-slate-400 text-sm">Pa raportime.</p>}
      </div>
    </div>
  );
}
